import React, { createContext, useReducer, useEffect } from 'react';
import type { ReactNode } from 'react';
import type { User, LoginCredentials, LoginResult } from '@/shared/lib/types';
import { authService } from '@/shared/lib/auth';
import { toast } from '@/shared/hooks/use-toast';
import { setAppInsightsUser, clearAppInsightsUser } from '@/shared/lib/appInsights';
import i18n from '@/i18n';

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

interface AuthContextType extends AuthState {
  login: (credentials: LoginCredentials) => Promise<LoginResult>;
  finalizeAfterMfa: (user: User) => void;
  logout: () => Promise<void>;
  refreshToken: () => Promise<void>;
  clearError: () => void;
  hasRole: (role: string) => boolean;
  hasAnyRole: (roles: string[]) => boolean;
  hasPermission: (permission: string) => boolean;
  hasPolicy: (policy: string) => boolean;
  isPlatformAdmin: () => boolean;
  isAdmin: () => boolean;
  isOperador: () => boolean;
  isVisualizador: () => boolean;
  isTenant: () => boolean;
  isStandAlone: () => boolean;
  canManageUsers: () => boolean;
  canManageClients: () => boolean;
  canEditDevices: () => boolean;
  canEditAlerts: () => boolean;
}

type AuthAction = 
  | { type: 'LOGIN_START' }
  | { type: 'LOGIN_SUCCESS'; payload: User }
  | { type: 'LOGIN_ERROR'; payload: string }
  | { type: 'LOGOUT' }
  | { type: 'REFRESH_SUCCESS'; payload: User }
  | { type: 'REFRESH_ERROR'; payload: string }
  | { type: 'CLEAR_ERROR' }
  | { type: 'SET_LOADING'; payload: boolean };

const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
};

function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'LOGIN_START':
      return {
        ...state,
        isLoading: true,
        error: null,
      };
    case 'LOGIN_SUCCESS':
      return {
        ...state,
        user: action.payload,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      };
    case 'LOGIN_ERROR':
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: action.payload,
      };
    case 'LOGOUT':
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      };
    case 'REFRESH_SUCCESS':
      return {
        ...state,
        user: action.payload,
        isAuthenticated: true,
        error: null,
      };
    case 'REFRESH_ERROR':
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        error: action.payload,
      };
    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null,
      };
    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.payload,
      };
    default:
      return state;
  }
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export { AuthContext };

const MFA_NUDGE_KEY = 'mfa_recommend_shown';

function suggestMfaIfMissing(user: User) {
  if (user.mfaEnabled) return;
  if (user.tipoUsuario === 'Admin') return; // Admin é forçado pelo backend
  if (sessionStorage.getItem(MFA_NUDGE_KEY) === '1') return;

  sessionStorage.setItem(MFA_NUDGE_KEY, '1');
  setTimeout(() => {
    toast({
      title: i18n.t('auth:authContext.mfaNudgeTitle'),
      description: i18n.t('auth:authContext.mfaNudgeDescription'),
    });
  }, 1500);
}

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        dispatch({ type: 'SET_LOADING', payload: true });
        
        const token = authService.getToken();
        const refreshToken = authService.getRefreshToken();
        const storedUser = authService.getUser();

        if (!token) {
          dispatch({ type: 'LOGOUT' });
          return;
        }
        
        if (!authService.isAuthenticated()) {
          if (refreshToken) {
            try {
              await authService.refreshToken();
              const user = await authService.getProfile();
              dispatch({ type: 'LOGIN_SUCCESS', payload: user });
              return;
            } catch {
              await authService.logout();
              dispatch({ type: 'LOGOUT' });
              return;
            }
          } else {
            await authService.logout();
            dispatch({ type: 'LOGOUT' });
            return;
          }
        }
        
        if (storedUser) {
          dispatch({ type: 'LOGIN_SUCCESS', payload: storedUser });
        }

        try {
          const serverUser = await authService.getProfile();
          dispatch({ type: 'LOGIN_SUCCESS', payload: serverUser });
          authService.setUser(serverUser);
        } catch (error: unknown) {

          const err = error as Record<string, unknown>;
          if (err?.status === 401 && refreshToken) {
            try {
              await authService.refreshToken();
              const serverUser = await authService.getProfile();
              dispatch({ type: 'LOGIN_SUCCESS', payload: serverUser });
              authService.setUser(serverUser);
            } catch (refreshError: unknown) {
              const refreshErr = refreshError as Record<string, unknown>;
              if (refreshErr?.status === 401) {
                localStorage.clear();
                await authService.logout();
                dispatch({ type: 'LOGOUT' });
              }
            }
          } else if (err?.status === 401 && !refreshToken) {
            await authService.logout();
            dispatch({ type: 'LOGOUT' });
          }
        }
      } catch {
        await authService.logout();
        dispatch({ type: 'LOGOUT' });
      } finally {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    };

    initializeAuth();
  }, []);

  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'auth_token' && e.newValue === null) {
        dispatch({ type: 'LOGOUT' });
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Sincroniza contexto de usuário com Application Insights
  useEffect(() => {
    if (state.isAuthenticated && state.user) {
      setAppInsightsUser({
        id: state.user.id,
        email: state.user.email,
        tenantId: state.user.tenantId,
      });
    } else {
      clearAppInsightsUser();
    }
  }, [state.isAuthenticated, state.user]);

  const finalizeAfterMfa = (user: User) => {
    dispatch({ type: 'LOGIN_SUCCESS', payload: user });
    toast({
      title: i18n.t('auth:authContext.loginSuccessTitle'),
      description: i18n.t('auth:authContext.welcomeMessage', { name: user.name }),
    });
    suggestMfaIfMissing(user);
  };

  const login = async (credentials: LoginCredentials): Promise<LoginResult> => {
    try {
      dispatch({ type: 'LOGIN_START' });

      const result = await authService.login(credentials);

      if (result.requiresMfa) {
        dispatch({ type: 'SET_LOADING', payload: false });
        return result;
      }

      dispatch({ type: 'LOGIN_SUCCESS', payload: result.auth.user });

      toast({
        title: i18n.t('auth:authContext.loginSuccessTitle'),
        description: i18n.t('auth:authContext.welcomeMessage', { name: result.auth.user.name }),
      });

      suggestMfaIfMissing(result.auth.user);

      return result;
    } catch (error: unknown) {
      let errorMessage = i18n.t('auth:authContext.loginErrorFallback');

      const err = error as Record<string, unknown>;
      if (err?.error && typeof err.error === 'object') {
        const errorObj = err.error as Record<string, unknown>;
        if (errorObj?.message && typeof errorObj.message === 'string') {
          errorMessage = errorObj.message;
        }
      } else if (err?.response && typeof err.response === 'object') {
        const response = err.response as Record<string, unknown>;
        if (response?.data && typeof response.data === 'object') {
          const data = response.data as Record<string, unknown>;
          if (data?.message && typeof data.message === 'string') {
            errorMessage = data.message;
          }
        }
      } else if (err?.message && typeof err.message === 'string') {
        errorMessage = err.message;
      }

      dispatch({ type: 'LOGIN_ERROR', payload: errorMessage });

      toast({
        title: i18n.t('auth:authContext.loginErrorTitle'),
        description: errorMessage,
        variant: "destructive",
      });

      throw error;
    }
  };

  const logout = async () => {
    try {
      await authService.logout();
      dispatch({ type: 'LOGOUT' });

      toast({
        title: i18n.t('auth:authContext.logoutTitle'),
        description: i18n.t('auth:authContext.logoutDescription'),
      });
    } catch {
      dispatch({ type: 'LOGOUT' });
    }
  };

  const refreshToken = async () => {
    try {
      await authService.refreshToken();

      const user = await authService.getProfile();
      dispatch({ type: 'REFRESH_SUCCESS', payload: user });
    } catch (error: unknown) {
      let errorMessage = i18n.t('auth:authContext.refreshErrorFallback');
      const err = error as Record<string, unknown>;
      if (err?.message && typeof err.message === 'string') {
        errorMessage = err.message;
      }
      dispatch({ type: 'REFRESH_ERROR', payload: errorMessage });

      toast({
        title: i18n.t('auth:authContext.sessionExpiredTitle'),
        description: i18n.t('auth:authContext.sessionExpiredDescription'),
        variant: "destructive",
      });

      throw error;
    }
  };

  const clearError = () => {
    dispatch({ type: 'CLEAR_ERROR' });
  };

  const hasRole = (role: string): boolean => {
    return authService.hasRole(role);
  };

  const hasAnyRole = (roles: string[]): boolean => {
    return authService.hasAnyRole(roles);
  };

  const hasPermission = (_permission: string): boolean => {
    // Permissions are not yet implemented in the User model.
    // Currently delegates to admin role check as a fallback.
    return authService.isAdmin();
  };

  const hasPolicy = (_policy: string): boolean => {
    // Policies are not yet implemented in the AuthService.
    // Currently delegates to admin role check as a fallback.
    return authService.isAdmin();
  };

  const isPlatformAdmin = (): boolean => authService.isPlatformAdmin();

  const isAdmin = (): boolean => {
    return authService.isAdmin();
  };

  const isOperador = (): boolean => {
    return authService.isOperador();
  };

  const isVisualizador = (): boolean => {
    return authService.isVisualizador();
  };

  const isTenant = (): boolean => {
    return authService.isTenant();
  };

  const isStandAlone = (): boolean => {
    return authService.isStandAlone();
  };

  const canManageUsers = (): boolean => {
    return authService.canManageUsers();
  };

  const canManageClients = (): boolean => {
    return authService.canManageClients();
  };

  const canEditDevices = (): boolean => {
    return authService.canEditDevices();
  };

  const canEditAlerts = (): boolean => {
    return authService.canEditAlerts();
  };

  const contextValue: AuthContextType = {
    ...state,
    login,
    finalizeAfterMfa,
    logout,
    refreshToken,
    clearError,
    hasRole,
    hasAnyRole,
    hasPermission,
    hasPolicy,
    isPlatformAdmin,
    isAdmin,
    isOperador,
    isVisualizador,
    isTenant,
    isStandAlone,
    canManageUsers,
    canManageClients,
    canEditDevices,
    canEditAlerts,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};